// Where the browser goes after the server has revoked the session.
//
// The server composes the IdP's logout URL (id_token_hint and, once a customer
// has registered one, post_logout_redirect_uri). A top-level navigation is the
// only delivery that survives third-party-cookie policy and frame-busting
// headers — the hidden iframe this replaces never loaded against Entra or Okta
// (X-Frame-Options: DENY / SAMEORIGIN), so sign-out silently did nothing.
export const nextLocationAfterLogout = ({ idpLogoutUrl, loginUrl }) =>
  idpLogoutUrl ? idpLogoutUrl : loginUrl

// The last step of a sign-out, once the server has revoked the session and
// the tokens are out of storage.
//
// Going to the IdP must NOT clear the signed-in claims first. Clearing them
// re-renders into the signed-out guard (page/component/auth-redirect.js),
// whose own window.location.replace('/auth/email') is newer than this
// assign -- and the browser keeps the newest navigation. Every SSO sign-out
// therefore landed on our login page with the IdP session still alive. The
// page is being left and the tokens are already gone, so the claims can
// simply go with it.
export const leaveAfterLogout = ({ idpLogoutUrl, loginUrl, clearClaims, navigate }) => {
  if (idpLogoutUrl) {
    navigate(idpLogoutUrl)
    return
  }
  clearClaims()
  navigate(loginUrl)
}

// Why a session ended, stashed for the login page.
//
// useLogout and the refresh handlers navigate to loginUrlFor(path, reason),
// but they clear the auth context first, and that re-render mounts the
// unauthenticated guard (page/component/auth-redirect.js), whose own
// window.location.replace(...) starts a second navigation. The browser keeps
// the newest one, so the login page lands with ?redirect= and no ?reason=.
// The stash survives that race: the login page reads it once when the query
// string carries no reason of its own.
export const LOGOUT_REASON_KEY = 'edxr-logout-reason'

export const stashLogoutReason = reason => {
  if (!reason) return
  try { sessionStorage.setItem(LOGOUT_REASON_KEY, reason) } catch (e) {}
}

// Reads and clears the stash; null when nothing was stashed.
export const consumeLogoutReason = () => {
  try {
    const reason = sessionStorage.getItem(LOGOUT_REASON_KEY)
    sessionStorage.removeItem(LOGOUT_REASON_KEY)
    return reason || null
  } catch (e) { return null }
}

// Require SSO ended the session (owner ruling 2026-10-29, Notion-style): the
// login is at a domain whose Require SSO applies and the session didn't come
// through the domain's own connection, so auth ended it and answers the
// refresh, the organization switch, a launch and a device approval with 401
// { message, reason: 'sso_required' }. The message names the connection
// ("Your organization now requires you to sign in with Contoso SSO.").
export const SSO_REQUIRED = 'sso_required'

// The server's sentence for a session it ended, kept beside the reason for
// the login page: in sessionStorage, never the address, so a crafted
// ?reason=sso_required link can only ever show our own fallback words.
export const LOGOUT_MESSAGE_KEY = 'edxr-logout-message'

export const stashLogoutMessage = error => {
  const response = responseOf(error)
  const body = response && response.data && typeof response.data === 'object' ? response.data : null
  const message = body && body.reason === SSO_REQUIRED && typeof body.message === 'string' ? body.message.trim() : ''
  if (!message) return
  try { sessionStorage.setItem(LOGOUT_MESSAGE_KEY, message) } catch (e) {}
}

// Reads and clears the message stash; null when nothing was stashed.
export const consumeLogoutMessage = () => {
  try {
    const message = sessionStorage.getItem(LOGOUT_MESSAGE_KEY)
    sessionStorage.removeItem(LOGOUT_MESSAGE_KEY)
    return message || null
  } catch (e) { return null }
}

// Extract the session-end reason from an axios error, if present.
// Returns 'idle', 'max', 'expired', 'impersonation_limit', 'sso_required',
// 'organization_unavailable', or null.
// 'sso_required': Require SSO ended the session (SSO_REQUIRED above).
// 'impersonation_limit' is an impersonated session past its 2 hours (owner
// ruling 2026-10-01): refresh and recreate refuse it with that reason. The server ends admin sessions
// on ADMIN_IDLE_TIMEOUT (idle) or ADMIN_SESSION_MAX (max) and answers the
// refresh with 401 { message, reason: 'idle' | 'max' }, having already
// revoked the refresh token. 'expired' is the same shape for a refresh token
// that simply ran out (a plain refresh, or RecreateAuthToken's /redirect/*
// deep-link handoff) — refreshRecreate forwards it here so an expired deep
// link lands on sign-in with a friendly notice instead of the generic error
// banner. Any other error → null.
//
// It reads both shapes a caller can hold: an axios error ({ response:
// { status, data } }) and the bare response api-form's actions throw
// (`throw exp.response`), which useRefresh receives.
const responseOf = error => (error && error.response) || (error && error.status !== undefined ? error : null)

export const sessionEndReason = error => {
  const response = responseOf(error)
  if (response && response.status === 401 && response.data && response.data.reason) {
    const reason = response.data.reason
    if (reason === 'idle' || reason === 'max' || reason === 'expired' || reason === 'impersonation_limit' || reason === SSO_REQUIRED) {
      return reason
    }
  }
  // The organization gate (core orggate) answers 401 with its own body
  // shape, { code: 'organization_unavailable' } rather than `reason` --
  // the session's organization was scheduled for deletion.
  // keepsSessionOnGateRefusal (api.service.js) still runs first in
  // checkAuthRedirect, so this only fires once the restore grace is over.
  if (response && response.status === 401 && response.data && response.data.code === 'organization_unavailable') {
    return 'organization_unavailable'
  }
  return null
}

// Why a REFRESH ended the session. Any 401 from auth-refresh means the
// refresh token is no good any more -- revoked from elsewhere (an admin's
// "Sign out", device revoke, a password change) answers 401 with no reason
// -- so it ends the session: the server's reason when it gave one, else
// 'ended'. null for anything else (a network failure, a 5xx), which keeps
// the session.
export const refreshEndReason = error => {
  const reason = sessionEndReason(error)
  if (reason) return reason
  const response = responseOf(error)
  return response && response.status === 401 ? 'ended' : null
}

// Build the query string for the login page, base64-encoding the redirect
// the same way useLoginRedirect's atob() reader expects.
//
// Built by hand rather than through URLSearchParams#set: the form-urlencoded
// serializer percent-encodes '=', which is harmless (useQueryParam's
// URLSearchParams#get decodes %3D back to '=' just fine) but makes the query
// string unnecessarily unreadable. The one byte that DOES need escaping is
// '+' -- left raw, a '+' in the base64 payload is read back by that same
// #get as a decoded space, corrupting the redirect before atob() ever sees
// it. '/' and '=' pass through #get unchanged either way, so only '+' is
// escaped here.
export const loginUrlFor = (pathname, reason) => {
  const parts = []
  if (pathname && !pathname.startsWith('/auth')) parts.push(`redirect=${btoa(pathname).replace(/\+/g, '%2B')}`)
  if (reason) parts.push(`reason=${reason}`)
  return `/auth/email${parts.length ? `?${parts.join('&')}` : ''}`
}

// The sign-out request body (POST /auth-logout): the refresh token travels in
// the body, never the URL. In an impersonated tab (the token carries
// impersonationEndsAt) signing out ends the WHOLE impersonation -- the
// device and launch sessions started from it too -- exactly as "End
// impersonation" does.
export const logoutBody = (refreshToken, claims) => {
  const body = { refreshToken, return: 'admin' }
  if (claims && claims.impersonationEndsAt) body.scope = 'impersonation'
  return body
}
